import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { PageHeading } from "../PageHeading/PageHeading.js";

/**
 * `<DataPageShell>` — canonical column-flex wrapper for any list page.
 *
 * Owns the load-bearing scroll-lock pattern from
 * `.vibe/60_TEMPLATE_DATA_PAGE.md` §"Sub-template 1":
 *   - `height: 100%` + `display: flex` + `flexDirection: column` so the
 *     toolbar pins to the top edge and the footer pins to the bottom
 *     edge while the table region owns the only scroll container.
 *   - `minHeight: 0` + `minWidth: 0` lets the inner flex item shrink
 *     below its content's intrinsic size (load-bearing — without it
 *     the DataGrid pushes the page wider than the viewport).
 *   - `overflowX: hidden` clips any residual horizontal spill so the
 *     toolbar + footer can't slide off-screen on horizontal scroll.
 *
 * Children render in three positional slots followed by any extras:
 *   1. Toolbar (inside a `flexShrink: 0` header region under the H1).
 *   2. Table region (gets `flex: 1` + `minHeight: 0`).
 *   3. Footer (sits at the bottom edge of the table region).
 *   4+. Extras — modals, snackbars, toasts. Rendered after the footer
 *       region so they sit at the page root for portal anchoring.
 *
 * The caller passes children as a positional array — the shell does not
 * inspect element types. Convention is `<Toolbar><Table><Footer>` in
 * that order; other arrangements work but layouts no longer pin
 * correctly.
 */
export interface DataPageShellProps {
  title: string;
  children: ReactNode;
}

export function DataPageShell({
  title,
  children,
}: DataPageShellProps): React.ReactElement {
  // Positional array — toolbar / table / footer + extras (modals,
  // snackbars). We render the first three into the three pre-sized flex
  // regions; anything after index 2 is rendered as a sibling of the
  // shell so portal-anchored components (modals, popovers) sit
  // alongside instead of inside the column-flex region.
  const slots: readonly ReactNode[] = Array.isArray(children)
    ? (children as readonly ReactNode[])
    : [children];
  const toolbar: ReactNode = slots[0] ?? null;
  const table: ReactNode = slots[1] ?? null;
  const footer: ReactNode = slots[2] ?? null;
  const extras: readonly ReactNode[] = slots.slice(3);

  return (
    <>
      <Box
        sx={{
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          // Load-bearing — let the inner flex children shrink below
          // their content's intrinsic size. Without it the DataGrid's
          // column row pushes the page wider than the viewport.
          minWidth: 0,
          // Load-bearing — clip any residual horizontal spill from the
          // DataGrid so the toolbar + footer stay pinned.
          overflowX: "hidden",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <PageHeading>{title}</PageHeading>
          </Box>
          {toolbar}
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex" }}>
            {table}
          </Box>
          {footer}
        </Box>
      </Box>
      {extras}
    </>
  );
}
